import React from 'react';
import styled from 'styled-components';
import { Link as RouterLink } from 'react-router-dom';
import api from '../api/axiosConfig';
import { useTranslation } from 'react-i18next';

/* ================== LAYOUT ================== */

const FooterWrap = styled.footer`
  width: min(100%, 1800px);
  margin: clamp(12px, 3vw, 32px) auto;
  padding: clamp(16px, 2.6vw, 28px);
  border-radius: 50px;

  background: rgba(0, 0, 0, 0.72);
  color: #fff;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);

  box-shadow: 0 4px 6px rgba(241, 134, 13, 0.79);
  overflow: hidden;
  box-sizing: border-box;
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: clamp(14px, 2vw, 22px);

  @media (max-width: 900px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ContactsBlock = styled(Block)`
  align-items: flex-start;

  @media (max-width: 900px) {
    align-items: center;
  }
`;

const SocialBlock = styled(Block)`
  margin-left: auto;
  align-items: flex-start;

  @media (max-width: 900px) {
    margin-left: 0;
    align-items: center;
  }
`;

/* ================== TEXT ================== */

const Title = styled.p`
  font-weight: 700;
  font-size: clamp(16px, 1.2vw, 18px);
  opacity: 0.95;
  margin: 0;
`;

const Muted = styled.p`
  margin: 0;
  font-size: 13px;
  opacity: 0.75;
`;

const Small = styled.span`
  font-size: 12px;
  opacity: 0.75;
`;

const BrandLine = styled.span`
  font-size: 13px;
  opacity: 0.9;
`;

/* ================== CONTACTS (PILLS) ================== */

const ContactRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;

  @media (max-width: 520px) {
    justify-content: center;
  }
`;

const Pill = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Label = styled.span`
  font-size: 12px;
  opacity: 0.7;
`;

const ContactValue = styled.a`
  color: #fff;
  text-decoration: none;
  font-size: 12px;

  &:hover {
    text-decoration: underline;
  }
`;

/* ================== FOLLOW ME (BUTTONS) ================== */

const SocialRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  @media (max-width: 900px) {
    justify-content: center;
  }
`;

const SocialButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  text-decoration: none;
  font-size: 14px;

  &:hover {
    transform: translateY(-1px);
  }
`;

const IconBox = styled.span`
  width: 18px;
  height: 18px;
  display: grid;
  place-items: center;
  flex: 0 0 18px;

  svg {
    width: 18px;
    height: 18px;
    display: block;
  }

  img {
    width: 18px;
    height: 18px;
    object-fit: contain;
    display: block;
  }
`;

/* ================== LINKS (KEEP AS YOU HAVE) ================== */

const LinksBar = styled.nav`
  margin-top: 20px;
  padding-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);

  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center; /* ✅ фикс “криво стоят” */
  gap: 10px 14px;

  text-align: center;
`;

const FooterLink = styled(RouterLink)`
  color: rgba(255, 255, 255, 0.88);
  text-decoration: none;
  font-size: 14px;
  display: inline-flex;
  align-items: center;

  &:hover {
    text-decoration: underline;
  }
`;

const Divider = styled.span`
  opacity: 0.35;
  line-height: 1; /* ✅ чтобы точка не прыгала */
`;

/* ================== BOTTOM ================== */

const Bottom = styled.div`
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);

  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 900px) {
    justify-content: center;
    text-align: center;
  }
`;

/* ================== ICONS ================== */

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M7.5 3h9A4.5 4.5 0 0 1 21 7.5v9A4.5 4.5 0 0 1 16.5 21h-9A4.5 4.5 0 0 1 3 16.5v-9A4.5 4.5 0 0 1 7.5 3Z"
      stroke="currentColor"
      strokeWidth="1.6"
    />
    <path d="M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const TelegramIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path d="M18.9 3.5 2.7 9.8c-1.1.4-1.1 1.1-.2 1.4l4.1 1.3 9.5-6c.4-.3.8-.1.5.2l-7.7 7 3.3 2.4c.6.4 1 .2 1.2-.5l2.1-9.8c.3-.8-.3-1.3-.9-1.1Z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.5 3.5A11 11 0 0 0 3.2 17.9L2 22l4.2-1.1A11 11 0 0 0 20.5 3.5Zm-8.9 18a9.2 9.2 0 0 1-4.7-1.3l-.3-.2-2.4.6.6-2.3-.2-.3A9.2 9.2 0 1 1 11.6 21.5Zm5.4-6.9c-.3-.2-1.8-.9-2.1-1s-.5-.2-.7.2-.8 1-.9 1.2-.3.2-.6.1a7.6 7.6 0 0 1-2.2-1.3 8.3 8.3 0 0 1-1.5-1.9c-.2-.3 0-.5.1-.6l.5-.6c.2-.2.2-.3.3-.5.1-.2 0-.4 0-.6s-.7-1.8-.9-2.4c-.2-.6-.4-.5-.7-.5h-.6c-.2 0-.6.1-.9.4s-1.2 1.1-1.2 2.8 1.2 3.2 1.4 3.5 2.3 3.6 5.6 5c.8.3 1.4.5 1.9.7.8.2 1.5.2 2.1.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4s-.2-.2-.5-.4Z" />
  </svg>
);

const EmailIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 4-8 5L4 8V6l8 5 8-5v2Z" />
  </svg>
);

const absMedia = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `http://127.0.0.1:8000${url}`;
};

const InlineSvg = ({ svg }) => (
  <span
    aria-hidden="true"
    dangerouslySetInnerHTML={{ __html: svg || '' }}
    style={{ display: 'grid', placeItems: 'center' }}
  />
);

const BuiltinIcon = ({ name }) => {
  if (name === 'instagram') return <InstagramIcon />;
  if (name === 'telegram') return <TelegramIcon />;
  if (name === 'whatsapp') return <WhatsAppIcon />;
  return <EmailIcon />;
};

const FollowIcon = ({ item }) => {
  const iconFileUrl = absMedia(item.icon_file_url);

  if (item.icon_kind === 'builtin') return <BuiltinIcon name={item.builtin_icon} />;
  if (item.icon_kind === 'upload' && iconFileUrl) return <img src={iconFileUrl} alt="" />;
  if (item.icon_kind === 'inline_svg' && item.inline_svg)
    return <InlineSvg svg={item.inline_svg} />;
  return <InstagramIcon />;
};

/* ================== FIX "/api/api/..." ================== */

function safeApiPath(path) {
  const p = String(path || '');
  if (!p) return p;

  const normalized = p.replace(/\/{2,}/g, '/');

  if (normalized.startsWith('/api/')) return normalized.replace(/^\/api/, '');
  if (normalized.startsWith('api/')) return normalized.replace(/^api/, '');

  if (normalized === '/api') return '/';
  if (normalized === 'api') return '/';

  return normalized;
}

/* ================== COMPONENT ================== */

export default function Footer() {
  const [contactsData, setContactsData] = React.useState(null);
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('it') ? 'it' : 'en';

  // pick(obj, "footer_contacts_title") -> obj.footer_contacts_title_it / obj.footer_contacts_title_en
  const pick = React.useCallback(
    (obj, key) => {
      if (!obj) return '';
      return (lang === 'it' ? obj[`${key}_it`] : obj[`${key}_en`]) || obj[`${key}_en`] || '';
    },
    [lang],
  );

  React.useEffect(() => {
    let alive = true;

    (async () => {
      try {
        // ВАЖНО: baseURL уже "/api", поэтому тут должен быть путь без "/api"
        const res = await api.get(safeApiPath('/contacts/page/'));
        if (!alive) return;
        setContactsData(res.data);
      } catch (e) {
        console.error('Footer load error:', e);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const d = contactsData || {};
  const cards = Array.isArray(d.cards) ? d.cards : [];

  const footerContacts = cards.filter(
    (c) => c.is_active && c.show_in_footer && c.footer_group === 'contacts',
  );

  const followLinks = Array.isArray(d.footer_links)
    ? d.footer_links.filter((l) => l.is_active)
    : [];

  return (
    <FooterWrap>
      <Top>
        <ContactsBlock>
          <Title>{pick(d, 'footer_contacts_title') || t('footer.contactsTitle')}</Title>

          <ContactRow>
            {footerContacts.map((c) => (
              <Pill key={c.id}>
                <Label>{c.name || t('footer.contactFallback')}</Label>

                <ContactValue
                  href={c.href || '#'}
                  target={c.open_in_new_tab ? '_blank' : undefined}
                  rel={c.open_in_new_tab ? 'noreferrer' : undefined}
                >
                  {c.value || ''}
                </ContactValue>
              </Pill>
            ))}
          </ContactRow>
        </ContactsBlock>

        <SocialBlock>
          <Title>{pick(d, 'footer_follow_title') || t('footer.followTitle')}</Title>

          <SocialRow>
            {followLinks.map((l) => (
              <SocialButton
                key={l.id}
                href={l.url || '#'}
                target={l.is_external ? '_blank' : undefined}
                rel={l.is_external ? 'noreferrer' : undefined}
              >
                <IconBox>
                  {l.title.toLowerCase().includes('instagram') && <InstagramIcon />}
                  {l.title.toLowerCase().includes('telegram') && <TelegramIcon />}
                </IconBox>

                {l.title}
              </SocialButton>
            ))}
          </SocialRow>
        </SocialBlock>
      </Top>

      {/* ✅ ТВОЙ LinksBar без изменений */}
      <LinksBar>
        <FooterLink to="/privacy">{t('footer.privacyPolicy')}</FooterLink>
        <Divider>•</Divider>
        <FooterLink to="/terms">{t('footer.termsOfService')}</FooterLink>
        <Divider>•</Divider>
        <FooterLink to="/map-site">{t('footer.siteNavigation')}</FooterLink>
      </LinksBar>

      <Bottom>
        <Muted>{pick(d, 'footer_designed_by') || t('footer.designedBy')}</Muted>
        <Small>{pick(d, 'footer_tagline') || t('footer.tagline')}</Small>
        <BrandLine>
          © {new Date().getFullYear()} {d.footer_brand || t('footer.brandFallback')}
        </BrandLine>
      </Bottom>
    </FooterWrap>
  );
}
